import * as React from "react"
import { Head, Link, useForm } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"

type Manufacturer = {
  id: number
  name: string
  canonical_name: string
  country: string | null
  notes: string | null
  is_own: boolean
  aliases: string[]
}

type PageProps = {
  manufacturer: Manufacturer
}

export default function Edit({ manufacturer }: PageProps) {
  const { data, setData, patch, processing, errors } = useForm({
    name: manufacturer.name ?? "",
    country: manufacturer.country ?? "",
    notes: manufacturer.notes ?? "",
    is_own: manufacturer.is_own ?? false,
  })

  const description = `Kurasi identitas manufaktur ${manufacturer.name}: nama, negara, catatan, dan penanda perusahaan sendiri.`

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/admin/manufacturers/${manufacturer.id}`)
  }

  return (
    <>
      <Head title={`Edit ${manufacturer.name}`}>
        <meta name="description" content={description} />
        <meta property="og:title" content={`Edit ${manufacturer.name}`} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <div className="mb-2 flex items-center gap-1.5 text-xs text-ink-muted">
          <span>Intelligence</span>
          <span>/</span>
          <Link href="/admin/manufacturers" className="hover:text-ink-body">
            Manufaktur
          </Link>
          <span>/</span>
          <Link href={`/admin/manufacturers/${manufacturer.id}`} className="hover:text-ink-body">
            {manufacturer.name}
          </Link>
          <span>/</span>
          <span className="text-ink-body font-medium">Edit</span>
        </div>

        <div className="border-b border-hairline pb-6">
          <h1>Edit identitas manufaktur</h1>
          <p className="mt-1 text-sm text-ink-muted">{description}</p>
        </div>

        <form onSubmit={onSubmit} className="mt-6 max-w-2xl">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="name">Nama manufaktur</label>
              <Input
                id="name"
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
                className="mt-1"
              />
              {errors.name && <p className="mt-1 text-sm text-danger">{errors.name}</p>}
              <p className="mt-1 text-xs text-ink-muted">
                Nama kanonik: <strong>{manufacturer.canonical_name}</strong> (dihitung otomatis dari
                nama).
              </p>
            </div>

            <div>
              <label htmlFor="country">Negara (opsional)</label>
              <Input
                id="country"
                value={data.country}
                onChange={(e) => setData("country", e.target.value)}
                placeholder="cth. Indonesia"
                className="mt-1"
              />
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="notes">Catatan (opsional)</label>
              <textarea
                id="notes"
                value={data.notes}
                onChange={(e) => setData("notes", e.target.value)}
                rows={3}
                className="form-control mt-1"
                placeholder="Konteks internal tentang manufaktur ini."
              />
            </div>

            <div className="sm:col-span-2">
              <label className="flex items-center gap-2 font-normal text-ink-body">
                <Checkbox
                  checked={data.is_own}
                  onChange={(e) => setData("is_own", e.target.checked)}
                />
                Perusahaan sendiri
              </label>
              <p className="mt-1 text-xs text-ink-muted">
                Tandai jika ini perusahaan kita (bukan kompetitor).
              </p>
            </div>

            {manufacturer.aliases.length > 0 && (
              <div className="sm:col-span-2">
                <span className="block text-sm font-medium text-ink-display">Alias (varian ejaan)</span>
                <div className="mt-2 flex flex-wrap gap-1">
                  {manufacturer.aliases.map((a) => (
                    <Badge key={a} tone="muted">
                      {a}
                    </Badge>
                  ))}
                </div>
                <p className="mt-1 text-xs text-ink-muted">
                  Varian yang dilipat saat sinkronisasi dari NIQ. Dikelola otomatis.
                </p>
              </div>
            )}
          </div>

          <div className="mt-8 flex items-center gap-3 border-t border-hairline pt-6">
            <Button type="submit" disabled={processing}>
              Simpan perubahan
            </Button>
            <Button asChild variant="ghost">
              <Link href={`/admin/manufacturers/${manufacturer.id}`}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Batal
              </Link>
            </Button>
          </div>
        </form>
      </AppShell>
    </>
  )
}
